import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import { motion } from "framer-motion";

// API Endpoints
const CATEGORIES_API = "https://server.havbit.in/api/categories";
const PRODUCTS_API = "https://server.havbit.in/api/public/products";

export default function MobileCategoryFlow() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null); 
    const [loading, setLoading] = useState(true);
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            
            // Fetch categories
            const catResponse = await fetch(CATEGORIES_API);
            const catData = await catResponse.json();
            setCategories(catData);
            
            // Create category map
            const idToNameMap = {};
            catData.forEach(cat => {
                idToNameMap[cat._id] = cat.name;
            });
            
            setCategoryMap(idToNameMap);
            
            // Set default selected category (first one)
            if (catData.length > 0) {
                setSelectedCategory(catData[0]);
            }
            
            // Fetch all products
            const prodResponse = await fetch(PRODUCTS_API);
            const prodResult = await prodResponse.json();
            const productsArray = prodResult.data || [];
            console.log("Products from API:", productsArray);
            setProducts(productsArray);
            
        } catch (error) {
            console.error("Error fetching data:", error);
            
            // Fallback categories agar API fail ho
            const FALLBACK_CATEGORIES = [
                { _id: '1', name: 'Bars & Bites', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766751846/products/fuujrigaubpxfuqhs2x5.png' },
                { _id: '2', name: 'Dry Fruits & Nuts', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766751985/products/nyk54xjlnerjgobfdgae.png' },
                { _id: '3', name: 'Superfoods', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766752030/products/amcx65vs31ewwizoajyc.png' },
                { _id: '4', name: 'Beverages', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766752340/products/nbtpvdcyfffse7xzunqq.png' },
                { _id: '5', name: 'Natural Products', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766752598/products/v9twecgy8l5chukiw9qw.png' },
                { _id: '6', name: 'Breakfast Essentials', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766752378/products/pmaawk72mvs8ehzsty6r.png' },
                { _id: '7', name: 'Combos & Gift Packs', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766752496/products/nxbunwjsj8briwudyvpv.png' },
                { _id: '8', name: 'Immunity & Wellness', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766752579/products/qesw0zc598mv00gkwfbs.png' },
                { _id: '9', name: 'Protein Bar', image: 'https://res.cloudinary.com/dsjkjd3k4/image/upload/v1766751897/products/wdtx575dofasyi7fxcac.png' },
            ];
            setCategories(FALLBACK_CATEGORIES);
            if (FALLBACK_CATEGORIES.length > 0) {
                setSelectedCategory(FALLBACK_CATEGORIES[0]);
            }
        } finally {
            setLoading(false);
        }
    };

    // Filter products when category changes
    useEffect(() => {
        if (selectedCategory && products.length > 0) {
            console.log("Selected Category ID:", selectedCategory._id);
            console.log("Selected Category Name:", selectedCategory.name);
            
            const filteredProducts = products.filter(product => {
                // Check by category ID
                if (product.category === selectedCategory._id) {
                    console.log("Matched by ID:", product.name);
                    return true;
                }
                
                // Check if product has vendor field that's an object
                if (typeof product.vendor === 'object') {
                    // If vendor is object, handle it differently
                    console.log("Product has vendor object:", product.name);
                }
                
                return false;
            });
            
            console.log("Filtered Products Count:", filteredProducts.length);
            console.log("All Products Count:", products.length);
            
            setCategoryProducts(filteredProducts);
        }
    }, [selectedCategory, products]);

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };

    // Safe vendor name extraction
    const getVendorName = (vendor) => {
        if (!vendor) return null;
        
        if (typeof vendor === 'string') {
            return vendor;
        }
        
        if (typeof vendor === 'object') {
            return vendor.name || vendor.restaurantName || vendor.email || 'Vendor';
        }
        
        return null;
    };

    if (loading) return (
        <div className="fixed inset-0 bg-white z-[40] flex items-center justify-center pb-20">
            <div className="w-6 h-6 border-2 border-red-100 border-t-red-600 rounded-full animate-spin"></div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[40] bg-white lg:hidden overflow-hidden flex flex-col mb-16 pt-[3.5rem]">
            {/* Header */}
            <div className="px-4 py-3 flex items-center gap-3 border-b border-gray-100 bg-white">
                <button onClick={() => navigate("/")} className="p-1">
                    <FiChevronLeft size={22} className="text-gray-800" />
                </button>
                <h1 className="text-base font-black text-gray-900 tracking-tight">Categories</h1>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Left Sidebar - Categories */}
                <div className="w-24 bg-white overflow-y-auto border-r border-gray-100">
                    {categories.map((cat) => (
                        <motion.div 
                            key={cat._id} 
                            onClick={() => handleCategoryClick(cat)}
                            whileTap={{ scale: 0.95 }}
                            className={`px-2 py-3 flex flex-col items-center gap-1.5 border-b border-gray-50 transition-all cursor-pointer ${
                                selectedCategory?._id === cat._id 
                                ? "bg-red-50 border-r-2 border-r-red-600" 
                                : "hover:bg-gray-50"
                            }`}
                        >
                            <div className="w-8 h-8 flex items-center justify-center">
                                <img 
                                    src={cat.image} 
                                    alt={cat.name} 
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                        e.target.src = `https://ui-avatars.com/api/?name=${cat.name}&background=random`;
                                    }}
                                />
                            </div>
                            <span className={`text-[9px] font-bold text-center uppercase tracking-tighter leading-tight line-clamp-2 ${
                                selectedCategory?._id === cat._id ? "text-red-600" : "text-gray-600"
                            }`}>
                                {cat.name}
                            </span>
                        </motion.div>
                    ))}
                </div>

                {/* Right Side - Products */}
                <div className="flex-1 flex flex-col bg-white">
                    {/* Category Title */}
                    <div className="px-3 py-3 border-b border-gray-100 bg-white">
                        <h1 className="text-sm font-black text-gray-900">
                            {selectedCategory?.name || "Select a Category"}
                        </h1>
                        <p className="text-[10px] text-gray-500 mt-0.5">
                            {categoryProducts.length} products found
                        </p>
                    </div>

                    {/* Products Grid */}
                    <div className="flex-1 overflow-y-auto p-3 bg-white">
                        {categoryProducts.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3">
                                {categoryProducts.map((prod) => {
                                    const vendorName = getVendorName(prod.vendor);
                                    const restaurantName = prod.restaurantName;
                                    
                                    return (
                                        <motion.div 
                                            key={prod._id}
                                            onClick={() => navigate(`/product/${prod._id}`)}
                                            whileTap={{ scale: 0.98 }}
                                            className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                        >
                                            <div className="w-full aspect-square mb-2 bg-gray-50 rounded-lg flex items-center justify-center p-2">
                                                <img 
                                                    src={prod.image} 
                                                    className="w-full h-full object-contain" 
                                                    alt={prod.name}
                                                    onError={(e) => {
                                                        e.target.src = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400";
                                                    }}
                                                />
                                            </div>
                                            <div className="px-0.5">
                                                <div className="flex justify-between items-start">
                                                    <p className="text-xs font-black text-gray-900">
                                                        ₹{prod.newPrice || prod.price || 0}
                                                    </p>
                                                    {prod.oldPrice && prod.oldPrice > (prod.newPrice || prod.price || 0) && (
                                                        <span className="text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-bold">
                                                            {Math.round(((prod.oldPrice - (prod.newPrice || prod.price)) / prod.oldPrice) * 100)}% OFF
                                                        </span>
                                                    )}
                                                </div>
                                                {prod.oldPrice && prod.oldPrice > (prod.newPrice || prod.price || 0) && (
                                                    <p className="text-[10px] text-gray-500 line-through mt-0.5">
                                                        ₹{prod.oldPrice}
                                                    </p>
                                                )}
                                                <p className="text-[10px] text-gray-600 font-medium line-clamp-2 leading-tight mt-1 min-h-[2.5rem]">
                                                    {prod.name}
                                                </p>
                                                {/* Safely render vendor/restaurant name */}
                                                {(vendorName || restaurantName) && (
                                                    <p className="text-[9px] text-gray-400 mt-1">
                                                        By {restaurantName || vendorName}
                                                    </p>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full py-10">
                                <div className="text-center">
                                    <p className="text-sm text-gray-400 font-medium">No products found in this category</p>
                                    <p className="text-xs text-gray-300 mt-1">
                                        Try selecting another category
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}